import type { CSSProperties } from 'react';
import { getColorValue } from '@/theme/colors';
import { zIndex } from '@/theme/zIndex';
import { Temper } from '@/types/frontend-types';
import {
  MESSAGE_MODAL_ACCENT_COLOR_BY_TEMPER,
  MESSAGE_MODAL_BACKDROP_BLUR_PX,
  MESSAGE_MODAL_CONTENT_DESKTOP_HEIGHT,
  MESSAGE_MODAL_FOOTER_GLOBAL_FLEX_DIRECTION,
  MESSAGE_MODAL_FOOTER_INLINE_FLEX_DIRECTION,
  MESSAGE_MODAL_GLOW_PULSE_MAX,
  MESSAGE_MODAL_SURFACE_BACKGROUND,
  MESSAGE_MODAL_SURFACE_DESKTOP_PADDING,
  MESSAGE_MODAL_SURFACE_RADIUS_PX,
  MESSAGE_MODAL_SURFACE_MOBILE_HEIGHT,
  MESSAGE_MODAL_SURFACE_MOBILE_JUSTIFY_CONTENT,
  MOBILE_CONTENT_MAX_HEIGHT,
  MOBILE_FOOTER_BUTTON_HEIGHT,
  MOBILE_FOOTER_BUTTON_PADDING,
  MOBILE_FOOTER_PADDING_BOTTOM,
  MOBILE_SINGLE_FOOTER_BUTTON_MAX_WIDTH,
  MOBILE_SURFACE_PADDING,
} from './messageModalConstants';

interface MessageModalAccentInput {
  temper: Temper;
  intensity: number;
}

interface MessageModalFooterButtonStyleInput {
  isGlobalLayout: boolean;
  isMobile: boolean;
  hasBothFooterButtons: boolean;
}

interface MessageModalFooterStyleInput {
  isGlobalLayout: boolean;
  isMobile: boolean;
}

interface MessageModalRootStyleInput {
  isMobile: boolean;
  temper: Temper;
  vh: number;
  vw: number;
}

interface MessageModalContainerStyleInput {
  isMobile: boolean;
  vh: number;
}

const DESKTOP_GLOBAL_DOUBLE_FOOTER_BUTTON_STYLE: CSSProperties = {
  maxWidth: '190px',
  width: 'calc(50% - 8px)',
};

const DESKTOP_GLOBAL_SINGLE_FOOTER_BUTTON_STYLE: CSSProperties = {
  width: 'fit-content',
};

const DESKTOP_INLINE_FOOTER_BUTTON_STYLE: CSSProperties = {
  maxWidth: '220px',
  width: '100%',
};

const MOBILE_DOUBLE_FOOTER_BUTTON_STYLE: CSSProperties = {
  width: 'calc(50% - 8px)',
};

const MOBILE_SINGLE_FOOTER_BUTTON_STYLE: CSSProperties = {
  maxWidth: MOBILE_SINGLE_FOOTER_BUTTON_MAX_WIDTH,
  width: '100%',
};

const SINGLE_FOOTER_BUTTON_STYLE_INDEX = 0;
const DOUBLE_FOOTER_BUTTON_STYLE_INDEX = 1;

const MOBILE_FOOTER_BUTTON_STYLE_BY_COUNT: readonly CSSProperties[] = [
  MOBILE_SINGLE_FOOTER_BUTTON_STYLE,
  MOBILE_DOUBLE_FOOTER_BUTTON_STYLE,
];

const DESKTOP_GLOBAL_FOOTER_BUTTON_STYLE_BY_COUNT: readonly CSSProperties[] = [
  DESKTOP_GLOBAL_SINGLE_FOOTER_BUTTON_STYLE,
  DESKTOP_GLOBAL_DOUBLE_FOOTER_BUTTON_STYLE,
];

const GLASS_BEVEL_BRIGHT =
  'inset -2px -3px 6px -2px rgba(255, 255, 255, 0.18), inset 1.5px 1.5px 1px 0 rgba(255, 255, 255, 0.34)';
const MESSAGE_MODAL_SURFACE_LIFT_SHADOW =
  '0 44px 120px -44px rgba(0, 0, 0, 0.86)';
const MESSAGE_MODAL_BACKDROP_BACKGROUND = 'rgba(0, 0, 0, 0.9)';

function getFooterButtonStyleIndex({
  hasBothFooterButtons,
}: Pick<MessageModalFooterButtonStyleInput, 'hasBothFooterButtons'>): number {
  if (hasBothFooterButtons) {
    return DOUBLE_FOOTER_BUTTON_STYLE_INDEX;
  }

  return SINGLE_FOOTER_BUTTON_STYLE_INDEX;
}

function getMessageModalAccent({
  temper,
  intensity,
}: MessageModalAccentInput): string {
  return getColorValue(MESSAGE_MODAL_ACCENT_COLOR_BY_TEMPER[temper], intensity);
}

export const MESSAGE_MODAL_SURFACE_SHADOW = [
  GLASS_BEVEL_BRIGHT,
  MESSAGE_MODAL_SURFACE_LIFT_SHADOW,
].join(', ');

export function getMessageModalOuterGlowStyle({
  temper,
}: Pick<MessageModalAccentInput, 'temper'>): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    borderRadius: `${MESSAGE_MODAL_SURFACE_RADIUS_PX}px`,
    boxShadow: `0 34px 70px -50px ${getMessageModalAccent({
      temper,
      intensity: 0.32 * MESSAGE_MODAL_GLOW_PULSE_MAX,
    })}`,
    pointerEvents: 'none',
    willChange: 'opacity',
  };
}

export function getMessageModalGlowLayerStyle({
  temper,
}: Pick<MessageModalAccentInput, 'temper'>): CSSProperties {
  return {
    width: '100%',
    height: '100%',
    backgroundImage: `radial-gradient(circle at 110% 106%, ${getMessageModalAccent({
      temper,
      intensity: 0.2 * MESSAGE_MODAL_GLOW_PULSE_MAX,
    })} 0%, ${getMessageModalAccent({
      temper,
      intensity: 0.06 * MESSAGE_MODAL_GLOW_PULSE_MAX,
    })} 32%, ${getMessageModalAccent({
      temper,
      intensity: 0.01 * MESSAGE_MODAL_GLOW_PULSE_MAX,
    })} 64%, rgba(0, 0, 0, 0) 82%)`,
    boxShadow: `${getMessageModalAccent({
      temper,
      intensity: 0.45 * MESSAGE_MODAL_GLOW_PULSE_MAX,
    })} 0 -12px 20px -4px inset`,
    willChange: 'opacity',
  };
}

export function getMessageModalFooterButtonStyle({
  isGlobalLayout,
  isMobile,
  hasBothFooterButtons,
}: MessageModalFooterButtonStyleInput): CSSProperties {
  const buttonCountStyleIndex = getFooterButtonStyleIndex({
    hasBothFooterButtons,
  });

  if (isMobile) {
    return { ...MOBILE_FOOTER_BUTTON_STYLE_BY_COUNT[buttonCountStyleIndex] };
  }

  if (!isGlobalLayout) {
    return { ...DESKTOP_INLINE_FOOTER_BUTTON_STYLE };
  }

  return { ...DESKTOP_GLOBAL_FOOTER_BUTTON_STYLE_BY_COUNT[buttonCountStyleIndex] };
}

export function getMessageModalSurfaceStyle(): CSSProperties {
  return {
    position: 'relative',
    zIndex: 1,
    backgroundColor: MESSAGE_MODAL_SURFACE_BACKGROUND,
    borderRadius: `${MESSAGE_MODAL_SURFACE_RADIUS_PX}px`,
    overflow: 'hidden',
  };
}

export function getMessageModalContentAreaHeight({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): string | undefined {
  if (isMobile) {
    return undefined;
  }

  return MESSAGE_MODAL_CONTENT_DESKTOP_HEIGHT;
}

export function getMessageModalContentAreaStyle({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): CSSProperties | undefined {
  if (!isMobile) {
    return undefined;
  }

  return {
    flex: '0 1 auto',
    maxHeight: MOBILE_CONTENT_MAX_HEIGHT,
    minHeight: 0,
  };
}

export function getMessageModalFooterStyle({
  isGlobalLayout,
  isMobile,
}: MessageModalFooterStyleInput): CSSProperties {
  return {
    display: 'flex',
    flexDirection: isGlobalLayout
      ? MESSAGE_MODAL_FOOTER_GLOBAL_FLEX_DIRECTION
      : MESSAGE_MODAL_FOOTER_INLINE_FLEX_DIRECTION,
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    paddingBottom: isMobile ? MOBILE_FOOTER_PADDING_BOTTOM : undefined,
  };
}

export function getMessageModalFooterButtonPadding({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): string | undefined {
  if (!isMobile) {
    return undefined;
  }

  return MOBILE_FOOTER_BUTTON_PADDING;
}

export function getMessageModalFooterButtonHeight({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): number | undefined {
  if (!isMobile) {
    return undefined;
  }

  return MOBILE_FOOTER_BUTTON_HEIGHT;
}

export function getMessageModalSurfaceHeight({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): string | undefined {
  if (!isMobile) {
    return undefined;
  }

  return MESSAGE_MODAL_SURFACE_MOBILE_HEIGHT;
}

export function getMessageModalSurfacePadding({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): string {
  return isMobile ? MOBILE_SURFACE_PADDING : MESSAGE_MODAL_SURFACE_DESKTOP_PADDING;
}

export function getMessageModalSurfaceJustifyContent({
  isMobile,
}: Pick<MessageModalFooterButtonStyleInput, 'isMobile'>): 'center' | undefined {
  if (!isMobile) {
    return undefined;
  }

  return MESSAGE_MODAL_SURFACE_MOBILE_JUSTIFY_CONTENT;
}

export function getMessageModalRootStyle({
  isMobile,
  temper,
  vh,
  vw,
}: MessageModalRootStyleInput): CSSProperties {
  const backgroundImage = isMobile
    ? `radial-gradient(120% 90% at 50% 0%, ${getMessageModalAccent({
      temper,
      intensity: 0.09,
    })} 0%, ${getMessageModalAccent({ temper, intensity: 0.02 })} 42%, rgba(0, 0, 0, 0) 78%)`
    : [
      `radial-gradient(60% 60% at 12% -6%, ${getMessageModalAccent({
        temper,
        intensity: 0.12,
      })} 0%, rgba(0, 0, 0, 0) 60%)`,
      `radial-gradient(60% 60% at 92% 108%, ${getMessageModalAccent({
        temper,
        intensity: 0.1,
      })} 0%, rgba(0, 0, 0, 0) 62%)`,
    ].join(', ');

  return {
    position: 'fixed',
    top: 0,
    left: 0,
    width: vw * 100,
    height: vh * 100,
    zIndex: zIndex.globalMessage,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    backgroundColor: MESSAGE_MODAL_BACKDROP_BACKGROUND,
    backgroundImage,
    backdropFilter: `blur(${MESSAGE_MODAL_BACKDROP_BLUR_PX}px)`,
    WebkitBackdropFilter: `blur(${MESSAGE_MODAL_BACKDROP_BLUR_PX}px)`,
  };
}

export function getMessageModalContainerStyle({
  isMobile,
  vh,
}: MessageModalContainerStyleInput): CSSProperties {
  return {
    position: 'relative',
    zIndex: 1,
    width: isMobile ? '100%' : '400px',
    height: isMobile ? '100%' : 'auto',
    maxHeight: isMobile ? '100%' : vh * 90,
    display: 'flex',
    flexDirection: 'column',
  };
}
